import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Check, ChevronRight, Sparkles, X } from "lucide-react";

const STORAGE_KEY = "batapp_onboarding_collapsed_v1";

export default function OnboardingPanel() {
  const [data, setData] = useState(null);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(STORAGE_KEY) === "1");

  useEffect(() => {
    let alive = true;
    const fetchIt = async () => {
      try {
        const { data } = await api.get("/onboarding/status");
        if (alive) setData(data);
      } catch {/* ignore */}
    };
    fetchIt();
    const onUpd = () => fetchIt();
    window.addEventListener("onboarding:refresh", onUpd);
    return () => { alive = false; window.removeEventListener("onboarding:refresh", onUpd); };
  }, []);

  if (!data || data.all_done) return null;

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
  };

  const pct = Math.round((data.completed / data.total) * 100);

  return (
    <div
      className="card animate-in mb-8 p-0 overflow-hidden"
      data-testid="onboarding-panel"
      style={{ borderColor: "#FFC400" }}
    >
      <button
        onClick={toggle}
        className="w-full flex items-center gap-3 px-5 py-3.5 text-left"
        style={{ background: "#FFC400", color: "#161618" }}
        data-testid="onboarding-toggle"
      >
        <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "#FFC400", color: "#F5F4F2" }}>
          <Sparkles size={15} strokeWidth={2.5} />
        </div>
        <div className="flex-1">
          <div className="font-display text-lg leading-tight tracking-tight" style={{ fontWeight: 600 }}>
            Mise en route — étape <span className="numerals" style={{ color: "#FFC400" }}>{data.next_step_index}</span> sur <span className="numerals">{data.total}</span>
          </div>
          <div className="text-[12px] opacity-80 font-body mt-0.5">
            {data.completed}/{data.total} étapes terminées · prêt à booster votre activité
          </div>
        </div>
        <div className="numerals text-xl" style={{ color: "#FFC400" }}>{pct}%</div>
        <ChevronRight size={18} className={`transition-transform ${collapsed ? "" : "rotate-90"}`} strokeWidth={2} />
      </button>

      {/* Progress bar */}
      <div className="h-1.5 bg-black/20">
        <div className="h-full transition-all" style={{ width: `${pct}%`, background: "#FFC400" }} />
      </div>

      {!collapsed && (
        <div className="p-5 space-y-2">
          {data.steps.map((s, i) => (
            <Link
              key={s.key}
              to={s.link}
              className="flex items-center gap-3 p-3 rounded-lg border transition-colors group"
              style={{
                borderColor: s.done ? "rgba(255,196,0,0.30)" : "#3A3D42",
                background: s.done ? "rgba(255,196,0,0.10)" : "transparent",
              }}
              data-testid={`onboarding-step-${s.key}`}
            >
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center font-mono text-xs font-bold shrink-0"
                style={{
                  background: s.done ? "#FFC400" : "#161618",
                  color: s.done ? "#161618" : "#F5F4F2",
                  border: `2px solid ${s.done ? "#FFC400" : "#3A3D42"}`,
                }}
              >
                {s.done ? <Check size={14} strokeWidth={3} /> : i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className={`font-display text-base tracking-tight ${s.done ? "line-through opacity-60" : ""}`} style={{ fontWeight: 600 }}>{s.label}</div>
                <div className="text-xs text-secondary-text font-body">{s.desc}</div>
              </div>
              {!s.done && (
                <span className="font-body text-[11px] font-medium opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: "#FFC400" }}>
                  Commencer →
                </span>
              )}
            </Link>
          ))}
          <div className="flex justify-end pt-2">
            <button
              onClick={() => { localStorage.setItem(STORAGE_KEY, "1"); setCollapsed(true); }}
              className="font-body text-[11px] font-medium text-tertiary-text hover:text-primary-text transition-colors"
              data-testid="onboarding-dismiss"
            >
              <X size={11} className="inline mr-1" /> Masquer pour l'instant
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
